import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import {
  ToastMessage,
  ToastOptions,
  SimpleMessage,
} from '@shared/types/message.type';

/**
 * Servicio para gestionar mensajes Toast usando PrimeNG
 * Adaptado para CondoPal con mensajes específicos del dominio
 */
@Injectable({
  providedIn: 'root'
})
export class AppMessageService {

  private defaultOptions: ToastOptions = {
    position: 'top-right',
    defaultLife: 5000,
    defaultSticky: false,
    showTransitionOptions: '300ms ease-out',
    hideTransitionOptions: '250ms ease-in'
  };

  constructor(
    private messageService: MessageService,
    private translate: TranslateService
  ) { }

  /**
   * Configurar opciones por defecto del servicio
   */
  setDefaultOptions(options: Partial<ToastOptions>): void {
    this.defaultOptions = { ...this.defaultOptions, ...options };
  }

  /**
   * Mostrar un mensaje genérico con configuración completa
   */
  showMessage(message: ToastMessage): void {
    this.messageService.add({
      severity: message.severity,
      summary: message.summary,
      detail: message.detail,
      life: message.life ?? this.defaultOptions.defaultLife,
      sticky: message.sticky ?? this.defaultOptions.defaultSticky,
      key: message.key ?? this.defaultOptions.defaultKey
    });
  }

  /**
   * Mostrar múltiples mensajes
   */
  showMessages(messages: ToastMessage[]): void {
    messages.forEach(message => this.showMessage(message));
  }

  // ==========================================================================
  // MÉTODOS DE CONVENIENCIA PARA DIFERENTES TIPOS DE MENSAJES
  // ==========================================================================

  /**
   * Mostrar mensaje de éxito
   */
  showSuccess(message: string | SimpleMessage, detail?: string): void {
    const { text, detail: msgDetail } = this.parseMessage(message, detail);
    this.showMessage({
      severity: 'success',
      summary: text,
      detail: msgDetail
    });
  }

  /**
   * Mostrar mensaje de error
   */
  showError(message: string | SimpleMessage, detail?: string): void {
    const { text, detail: msgDetail } = this.parseMessage(message, detail);
    this.showMessage({
      severity: 'error',
      summary: text,
      detail: msgDetail,
      life: 8000 // Los errores duran más tiempo
    });
  }

  /**
   * Mostrar mensaje informativo
   */
  showInfo(message: string | SimpleMessage, detail?: string): void {
    const { text, detail: msgDetail } = this.parseMessage(message, detail);
    this.showMessage({
      severity: 'info',
      summary: text,
      detail: msgDetail
    });
  }

  /**
   * Mostrar mensaje de advertencia
   */
  showWarning(message: string | SimpleMessage, detail?: string): void {
    const { text, detail: msgDetail } = this.parseMessage(message, detail);
    this.showMessage({
      severity: 'warn',
      summary: text,
      detail: msgDetail,
      life: 7000 // Las advertencias duran un poco más
    });
  }

  /**
   * Limpiar todos los mensajes
   */
  clear(): void {
    this.messageService.clear();
  }

  /**
   * Limpiar mensajes por clave
   */
  clearByKey(key: string): void {
    this.messageService.clear(key);
  }

  // ==========================================================================
  // MÉTODOS UTILITARIOS PRIVADOS
  // ==========================================================================

  /**
   * Parsear mensaje que puede ser string o objeto SimpleMessage
   * Se encarga de traducir las llaves i18n si existen
   */
  private parseMessage(message: string | SimpleMessage, fallbackDetail?: string): { text: string; detail?: string } {
    let text = '';
    let detail = fallbackDetail;

    if (typeof message === 'string') {
      text = this.translate.instant(message);
    } else {
      text = this.translate.instant(message.text);
      detail = message.detail ? this.translate.instant(message.detail) : fallbackDetail;
    }

    // Si el detalle es una llave i18n, intentar traducirla también
    if (detail) {
      detail = this.translate.instant(detail);
    }

    return { text, detail };
  }
}
